// Dimensionnement CVC d'un dossier TRAME — état du projet, lecture bornée de la saisie et calcul. Code PARTAGÉ :
// l'écran l'exécute pour l'affichage immédiat, le serveur le réexécute à chaque enregistrement et SON résultat fait foi
// (note de calcul, base de données).
//
// REMPLACE etat.mjs de VLT Devis (public/outils/metiers/cvc/etat.mjs, commit 53f27dc du 18/09/2026, SHA-256 du source
// 62A4FA3669F6365E9666D705051BD1137A59A41C9C869EE99FE771248C73823A), écarté par la recette du 06/10/2026 (§ 9.4) : on en
// garde la lecture bornée, l'appel des moteurs, les alertes et les résultats ; on n'en garde NI les prix, NI les lignes de
// devis, NI la nomenclature (voir chiffrage.ts, copie de référence non branchée).
//
// Adaptations TRAME (à valider par le dirigeant) :
//   B1. Bornes de saisie relevées pour l'ERP : jusqu'à 2 000 personnes par local (500 à l'origine), 60 locaux (30),
//       12 vitrages par local (6), 60 zones et 30 groupes extérieurs.
//   B2. Débit d'air neuf absent sur un local d'ERP occupé : alerte V01 (la charge de l'air neuf n'est pas comptée ; on
//       n'invente pas un débit réglementaire).
//   B3. Émetteurs à eau existants (puissance nominale par local) comparés aux déperditions au régime d'eau saisi
//       (loi des émetteurs, chaud.ts A4) : alerte E02 si la puissance réelle est inférieure aux déperditions.
//
// Unités de saisie : mètres et m², °C, % HR, m³/h, vol/h, watts. AUCUNE valeur de chantier ni d'entreprise n'est codée
// en dur. Un champ vide n'est JAMAIS compté zéro : la donnée manquante est dite et le résultat reste « à compléter ».

import * as F from './froid.ts';
import * as C from './chaud.ts';
import { calculerZone, calculerGroupe, totaux, type ResultatGroupe, type ResultatZone } from './zones.ts';
import { LISTE_DEPARTEMENTS, SOURCE_ZONES, zoneDuDepartement } from './zones-climatiques.ts';

export const SCHEMA = 'trame-cvc/1';
export { F as FROID, C as CHAUD, LISTE_DEPARTEMENTS, SOURCE_ZONES, zoneDuDepartement };

export const MAX = Object.freeze({ texte: 200, pieces: 60, zones: 60, groupes: 30, vitrages: 12, justification: 400 });

export const BORNES = Object.freeze({
  surface: [0.5, 5000],
  cote: [0.2, 300],
  hauteur: [1.5, 30],
  count: [1, 200],
  facades: [0, 4],
  temperature: [-30, 60],
  humidite: [0, 100],
  altitude: [0, 3000],
  debit: [0, 100000],
  infiltration: [0, 5],
  pourcentage: [0, 100],
  securite: [0, 30],
  simultaneite: [0.5, 1],
  puissance: [0, 1000000],
  personnes: [0, 2000],          // B1 : 500 à l'origine
  u: [0.05, 10],
  pont_thermique: [0, 1],
  apport_occupant: [0, 500],
  soufflage: [5, 40],
  eau: [20, 95],
  exposant: [1, 2],
} as const satisfies Record<string, readonly [number, number]>);

export const OBJECTIFS = Object.freeze([
  { id: 'froid', nom: 'Rafraîchissement seul', chaud: false },
  { id: 'froid_chaud', nom: 'Froid et chauffage (réversible)', chaud: true },
  { id: 'chauffage_principal', nom: 'Chauffage principal (pompe à chaleur)', chaud: true },
]);

export const TERMINAUX_ZONE = Object.freeze([
  { id: 'murale', nom: 'Unité murale' },
  { id: 'console', nom: 'Console' },
  { id: 'cassette', nom: 'Cassette' },
  { id: 'gainable', nom: 'Gainable' },
  { id: 'cta', nom: 'Centrale de traitement d’air' },
  { id: 'emetteurs_eau', nom: 'Émetteurs à eau (radiateurs, plancher)' },
  { id: 'a_definir', nom: 'À définir' },
]);

/** Réglages exposés à l'écran, avec leur libellé, leur unité et leur origine. Toutes des hypothèses logiciel modifiables. */
export const REGLAGES_INFO: readonly { cle: keyof Reglages; libelle: string; unite: string; source: string }[] = Object.freeze([
  { cle: 'dt_solaire_mur_k', libelle: 'Échauffement solaire équivalent des murs', unite: 'K', source: 'Hypothèse logiciel de l’outil d’origine' },
  { cle: 'dt_solaire_toit_k', libelle: 'Échauffement solaire équivalent d’une toiture exposée', unite: 'K', source: 'Hypothèse logiciel de l’outil d’origine' },
  { cle: 'dt_comble_k', libelle: 'Comble perdu : écart à la température extérieure', unite: 'K', source: 'Hypothèse logiciel de l’outil d’origine' },
  { cle: 'occupant_sensible_w', libelle: 'Apport sensible par occupant (activité courante)', unite: 'W', source: F.SOURCES_APPORTS_OCCUPANT.courante },
  { cle: 'occupant_latent_w', libelle: 'Apport latent par occupant (activité courante)', unite: 'W', source: F.SOURCES_APPORTS_OCCUPANT.courante },
  { cle: 'occupant_sensible_assis_w', libelle: 'Apport sensible par occupant (public assis : culte, classe, réunion)', unite: 'W', source: F.SOURCES_APPORTS_OCCUPANT.assis },
  { cle: 'occupant_latent_assis_w', libelle: 'Apport latent par occupant (public assis : culte, classe, réunion)', unite: 'W', source: F.SOURCES_APPORTS_OCCUPANT.assis },
  { cle: 'occupant_sensible_atelier_w', libelle: 'Apport sensible par occupant (atelier)', unite: 'W', source: F.SOURCES_APPORTS_OCCUPANT.atelier },
  { cle: 'occupant_latent_atelier_w', libelle: 'Apport latent par occupant (atelier)', unite: 'W', source: F.SOURCES_APPORTS_OCCUPANT.atelier },
  { cle: 'poste_info_w', libelle: 'Puissance dissipée par poste informatique', unite: 'W', source: 'Hypothèse logiciel de l’outil d’origine' },
  { cle: 'tv_w', libelle: 'Puissance dissipée par écran de télévision', unite: 'W', source: 'Hypothèse logiciel de l’outil d’origine' },
  { cle: 'porte_u', libelle: 'Coefficient U des portes', unite: 'W/m².K', source: 'Hypothèse logiciel de l’outil d’origine' },
  { cle: 'capacite_air_wh_m3k', libelle: 'Capacité thermique volumique de l’air (ρ × c)', unite: 'Wh/m³.K', source: 'Valeur physique usuelle (0,34)' },
  { cle: 'recuperation_pct', libelle: 'Rendement de récupération par défaut (double flux)', unite: '%', source: 'Zéro tant qu’il n’est pas saisi (pas de récupération supposée)' },
]);

/* ------------------------------------------ types ------------------------------------------ */

export type Reglages = F.ReglagesFroid & C.ReglagesChaud & { confirmees: boolean };

export interface PieceProjet extends F.Piece {
  /** B3 — somme des puissances nominales des émetteurs à eau du local (W, au régime nominal du catalogue). */
  emetteurs_nominal_w?: number | null;
}

export interface ProjetCvc {
  schema: typeof SCHEMA;
  objectif: string;
  batiment: { type: string; departement: string; altitude_m: number | null; ponts: string };
  conditions: F.Conditions & { theta_e_force: number | null; theta_e_source: string };
  pieces: PieceProjet[];
  zones: { id: string; nom: string; terminal: string; terminaux: number }[];
  groupes: { id: string; nom: string; zones: string[] }[];
  securite: { froid_pct: number; chaud_pct: number };
  simultaneite: { s: number; justification: string };
  reglages: Reglages;
  emetteurs: { depart_c: number | null; retour_c: number | null; exposant: number; moyenne: C.MoyenneEmetteur;
    nominal_depart_c: number; nominal_retour_c: number; nominal_ambiance_c: number };
}

export type Alerte = F.AlerteCvc;

/* ------------------------------------- lecture bornée ------------------------------------- */

const estNombre = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
export const arrondi = (x: number | null | undefined, d = 2): number | null => (estNombre(x) ? Math.round(x * 10 ** d) / 10 ** d : null);

export const nf = (x: number | null | undefined, d = 1) => (x == null || !Number.isFinite(x) ? '—' : arrondi(x, d)!.toLocaleString('fr-FR', { maximumFractionDigits: d }));
export const kw = (w: number | null | undefined, d = 2) => (w == null || !Number.isFinite(w) ? '—' : nf(w / 1000, d) + ' kW');

/** Lit un nombre saisi (virgule française acceptée). Hors bornes ou illisible → undefined ; vide → null. */
export function lireNombre(txt: unknown, { min = -Infinity, max = Infinity, dec = 3, entier = false }: { min?: number; max?: number; dec?: number; entier?: boolean } = {}): number | null | undefined {
  const s = String(txt == null ? '' : txt).trim().replace(/\s/g, '').replace(',', '.');
  if (s === '') return null;
  if (!/^-?\d*\.?\d*$/.test(s) || s === '.' || s === '-') return undefined;
  const v = Number(s);
  if (!Number.isFinite(v) || v < min || v > max) return undefined;
  if (entier && !Number.isInteger(v)) return undefined;
  return arrondi(v, dec);
}

const nombre = (v: unknown, bornes?: readonly [number, number], dec = 3): number | null => {
  if (!estNombre(v)) return null;
  if (bornes && (v < bornes[0] || v > bornes[1])) return null;
  return arrondi(v, dec);
};
const texte = (v: unknown, max: number = MAX.texte) => String(v == null ? '' : v).slice(0, max);
const choix = (v: unknown, liste: readonly ({ id: string } | string)[], defaut: string): string =>
  (liste.some((x) => (typeof x === 'string' ? x : x.id) === v) ? v as string : defaut);
const objet = (v: unknown): Record<string, any> => (v && typeof v === 'object' && !Array.isArray(v) ? v as Record<string, any> : {});
let compteur = 0;
const nouvelId = (p: string) => p + '-' + Date.now().toString(36) + '-' + (++compteur).toString(36) + Math.random().toString(36).slice(2, 6);

/* --------------------------------------- état --------------------------------------- */

export function nouvellePiece(nom = 'Local 1'): PieceProjet {
  return {
    id: nouvelId('p'), nom, type: 'sejour',
    surface_m2: null, longueur_m: null, largeur_m: null, hauteur_m: 2.5, count: 1,
    position: 'intermediaire', plancher_bas: 'local_chauffe', facades: null,
    isolation: 'inconnue', vitrage_defaut: 'double',
    vitrages: [], portes_m2: null,
    occupants: null, occupant_sensible_w: null, occupant_latent_w: null,
    eclairage_w: null, postes_info: null, tv: null,
    equipements_w: null, equipements_latent_w: null,
    ventilation_m3h: null, air_neuf_pretraite: false, soufflage_t: null, soufflage_hr: null,
    infiltration_volh: null, recuperation_pct: null,
    theta_int_c: null, intermittence: false, emetteurs_nominal_w: null,
    zone_id: null,
  };
}

export function nouvelleZone(nom = 'Zone 1') {
  return { id: nouvelId('z'), nom, terminal: 'a_definir', terminaux: 1 };
}

export function nouvelEtat(): ProjetCvc {
  const piece = nouvellePiece();
  const zone = nouvelleZone('Zone 1');
  piece.zone_id = zone.id;
  return {
    schema: SCHEMA,
    objectif: 'froid_chaud',
    batiment: { type: 'logement', departement: '', altitude_m: null, ponts: 'standard' },
    conditions: { ...F.CONDITIONS_DEFAUT, theta_e_force: null, theta_e_source: '' } as ProjetCvc['conditions'],
    pieces: [piece],
    zones: [zone],
    groupes: [{ id: nouvelId('g'), nom: 'Groupe extérieur 1', zones: [zone.id] }],
    securite: { froid_pct: 10, chaud_pct: 10 },
    simultaneite: { s: 1, justification: '' },
    reglages: { ...F.REGLAGES_FROID, ...C.REGLAGES_CHAUD, confirmees: false },
    emetteurs: { depart_c: null, retour_c: null, exposant: C.EXPOSANT_EMETTEUR_DEFAUT, moyenne: 'arithmetique',
      nominal_depart_c: C.REGIME_NOMINAL_EMETTEUR.depart_c, nominal_retour_c: C.REGIME_NOMINAL_EMETTEUR.retour_c,
      nominal_ambiance_c: C.REGIME_NOMINAL_EMETTEUR.ambiance_c },
  };
}

function normaliserVitrage(v: unknown): F.Vitrage {
  const o = objet(v);
  return {
    id: texte(o.id, 40) || nouvelId('v'),
    nom: texte(o.nom, 60),
    orientation: choix(o.orientation, F.ORIENTATIONS, 'Sud'),
    largeur_m: nombre(o.largeur_m, BORNES.cote),
    hauteur_m: nombre(o.hauteur_m, BORNES.cote),
    nombre: nombre(o.nombre, [1, 200], 0),
    vitrage: choix(o.vitrage, F.VITRAGES, 'double'),
    protection: choix(o.protection, F.PROTECTIONS, 'aucune'),
    irradiance_w_m2: nombre(o.irradiance_w_m2, [0, 1500], 0),
  };
}

function normaliserPiece(p: unknown, zonesIds: string[]): PieceProjet {
  const base = nouvellePiece();
  const o = objet(p);
  return {
    ...base,
    id: texte(o.id, 40) || base.id,
    nom: texte(o.nom) || base.nom,
    type: choix(o.type, F.TYPES_PIECE, 'sejour'),
    surface_m2: nombre(o.surface_m2, BORNES.surface),
    longueur_m: nombre(o.longueur_m, BORNES.cote),
    largeur_m: nombre(o.largeur_m, BORNES.cote),
    hauteur_m: nombre(o.hauteur_m, BORNES.hauteur) == null ? base.hauteur_m : nombre(o.hauteur_m, BORNES.hauteur),
    count: nombre(o.count, BORNES.count, 0) || 1,
    position: choix(o.position, F.POSITIONS, 'intermediaire'),
    plancher_bas: choix(o.plancher_bas, C.PLANCHERS_BAS, 'local_chauffe'),
    facades: nombre(o.facades, BORNES.facades, 0),
    isolation: choix(o.isolation, F.ISOLATIONS, 'inconnue'),
    vitrage_defaut: choix(o.vitrage_defaut, F.VITRAGES, 'double'),
    vitrages: (Array.isArray(o.vitrages) ? o.vitrages : []).slice(0, MAX.vitrages).map(normaliserVitrage),
    portes_m2: nombre(o.portes_m2, [0, 200]),
    occupants: nombre(o.occupants, BORNES.personnes, 0),
    occupant_sensible_w: nombre(o.occupant_sensible_w, BORNES.apport_occupant, 1),
    occupant_latent_w: nombre(o.occupant_latent_w, BORNES.apport_occupant, 1),
    eclairage_w: nombre(o.eclairage_w, BORNES.puissance, 0),
    postes_info: nombre(o.postes_info, [0, 1000], 0),
    tv: nombre(o.tv, [0, 1000], 0),
    equipements_w: nombre(o.equipements_w, BORNES.puissance, 0),
    equipements_latent_w: nombre(o.equipements_latent_w, BORNES.puissance, 0),
    ventilation_m3h: nombre(o.ventilation_m3h, BORNES.debit, 0),
    air_neuf_pretraite: o.air_neuf_pretraite === true,
    soufflage_t: nombre(o.soufflage_t, BORNES.soufflage, 1),
    soufflage_hr: nombre(o.soufflage_hr, BORNES.humidite, 0),
    infiltration_volh: nombre(o.infiltration_volh, BORNES.infiltration),
    recuperation_pct: nombre(o.recuperation_pct, BORNES.pourcentage, 0),
    theta_int_c: nombre(o.theta_int_c, [5, 35]),
    intermittence: o.intermittence === true,
    emetteurs_nominal_w: nombre(o.emetteurs_nominal_w, BORNES.puissance, 0),
    zone_id: zonesIds.includes(o.zone_id) ? o.zone_id : zonesIds[0] || null,
  };
}

/** Lecture bornée de TOUTE saisie (écran, base, requête) : hors bornes → vide, jamais une valeur inventée. Idempotent. */
export function normaliser(brut: unknown): ProjetCvc {
  const o = objet(brut);
  const base = nouvelEtat();

  const vus = new Set<string>();
  let zones = (Array.isArray(o.zones) ? o.zones : []).slice(0, MAX.zones).map((z: unknown) => {
    const x = objet(z);
    let id = texte(x.id, 40) || nouvelId('z');
    if (vus.has(id)) id = nouvelId('z');
    vus.add(id);
    return {
      id,
      nom: texte(x.nom, 80) || 'Zone',
      terminal: choix(x.terminal, TERMINAUX_ZONE, 'a_definir'),
      terminaux: nombre(x.terminaux, [1, 50], 0) || 1,
    };
  });
  if (!zones.length) zones = base.zones;
  const zonesIds = zones.map((z) => z.id);

  let pieces = (Array.isArray(o.pieces) ? o.pieces : []).slice(0, MAX.pieces).map((p: unknown) => normaliserPiece(p, zonesIds));
  if (!pieces.length) pieces = [normaliserPiece({ nom: 'Local 1' }, zonesIds)];

  const groupes = (Array.isArray(o.groupes) && o.groupes.length ? o.groupes : [{ id: nouvelId('g'), nom: 'Groupe extérieur 1', zones: zonesIds }])
    .slice(0, MAX.groupes).map((g: unknown) => {
      const x = objet(g);
      return {
        id: texte(x.id, 40) || nouvelId('g'),
        nom: texte(x.nom, 80) || 'Groupe extérieur',
        zones: [...new Set<string>((Array.isArray(x.zones) ? x.zones : zonesIds).filter((z: unknown) => zonesIds.includes(z as string)))],
      };
    });

  const cond = objet(o.conditions);
  const reg = objet(o.reglages);
  const reglages: Reglages = { ...base.reglages };
  for (const k of Object.keys(base.reglages) as (keyof Reglages)[]) {
    if (k === 'confirmees') continue;
    const v = nombre(reg[k], k === 'securite_pct' ? BORNES.securite : [0, 100000], 4);
    if (v != null) (reglages as any)[k] = v;
  }
  reglages.confirmees = reg.confirmees === true;
  const em = objet(o.emetteurs);
  const bat = objet(o.batiment);
  const sec = objet(o.securite);
  const sim = objet(o.simultaneite);

  return retirerZonesVides({
    schema: SCHEMA,
    objectif: choix(o.objectif, OBJECTIFS, 'froid_chaud'),
    batiment: {
      type: texte(bat.type, 40) || 'logement',
      departement: texte(bat.departement, 3).toUpperCase(),
      altitude_m: nombre(bat.altitude_m, BORNES.altitude, 0),
      ponts: choix(bat.ponts, C.PONTS_THERMIQUES, 'standard'),
    },
    conditions: {
      ext_t: nombre(cond.ext_t, BORNES.temperature, 1) ?? base.conditions.ext_t,
      ext_valeur: nombre(cond.ext_valeur, BORNES.humidite, 1) ?? base.conditions.ext_valeur,
      ext_mode: 'hr',
      int_t: nombre(cond.int_t, BORNES.temperature, 1) ?? base.conditions.int_t,
      int_valeur: nombre(cond.int_valeur, BORNES.humidite, 1) ?? base.conditions.int_valeur,
      int_mode: 'hr',
      pression: base.conditions.pression,
      source: base.conditions.source,
      verifiee: cond.verifiee === true,
      theta_e_force: nombre(cond.theta_e_force, [-40, 20], 1),
      theta_e_source: texte(cond.theta_e_source, MAX.justification),
    },
    pieces, zones, groupes,
    securite: {
      froid_pct: nombre(sec.froid_pct, BORNES.securite, 1) ?? 10,
      chaud_pct: nombre(sec.chaud_pct, BORNES.securite, 1) ?? 10,
    },
    simultaneite: {
      s: nombre(sim.s, BORNES.simultaneite, 2) ?? 1,
      justification: texte(sim.justification, MAX.justification),
    },
    reglages,
    emetteurs: {
      depart_c: nombre(em.depart_c, BORNES.eau, 1),
      retour_c: nombre(em.retour_c, BORNES.eau, 1),
      exposant: nombre(em.exposant, BORNES.exposant, 2) ?? C.EXPOSANT_EMETTEUR_DEFAUT,
      moyenne: choix(em.moyenne, ['arithmetique', 'logarithmique'], 'arithmetique') as C.MoyenneEmetteur,
      nominal_depart_c: nombre(em.nominal_depart_c, BORNES.eau, 1) ?? C.REGIME_NOMINAL_EMETTEUR.depart_c,
      nominal_retour_c: nombre(em.nominal_retour_c, BORNES.eau, 1) ?? C.REGIME_NOMINAL_EMETTEUR.retour_c,
      nominal_ambiance_c: nombre(em.nominal_ambiance_c, [5, 35], 1) ?? C.REGIME_NOMINAL_EMETTEUR.ambiance_c,
    },
  });
}

/* ----------------------------- ajouts et suppressions ----------------------------- */

const copie = <T>(x: T): T => JSON.parse(JSON.stringify(x));

/** Ajoute un local. Sans zone indiquée, il ouvre sa propre zone (rattachée au premier groupe extérieur). */
export function ajouterPiece(etat: ProjetCvc, zoneId?: string): ProjetCvc {
  const e = copie(etat);
  if (e.pieces.length >= MAX.pieces) return e;
  const p = nouvellePiece('Local ' + (e.pieces.length + 1));
  if (zoneId && e.zones.some((z) => z.id === zoneId)) {
    p.zone_id = zoneId;
  } else {
    if (e.zones.length >= MAX.zones) return e;
    const z = nouvelleZone('Zone ' + (e.zones.length + 1));
    p.zone_id = z.id;
    e.zones.push(z);
    if (e.groupes[0]) e.groupes[0].zones.push(z.id);
  }
  e.pieces.push(p);
  return e;
}

/* Une zone sans aucun local ne sert plus à rien : elle est retirée. Une zone au moins est toujours conservée. */
function retirerZonesVides(e: ProjetCvc): ProjetCvc {
  const occupees = new Set(e.pieces.map((p) => p.zone_id));
  const restantes = e.zones.filter((z) => occupees.has(z.id));
  if (!restantes.length || restantes.length === e.zones.length) return e;
  const gardees = new Set(restantes.map((z) => z.id));
  e.zones = restantes;
  e.groupes = e.groupes.map((g) => ({ ...g, zones: g.zones.filter((z) => gardees.has(z)) }));
  return e;
}

export function supprimerPiece(etat: ProjetCvc, pieceId: string): ProjetCvc {
  const e = copie(etat);
  if (!e.pieces.some((x) => x.id === pieceId) || e.pieces.length <= 1) return e;
  e.pieces = e.pieces.filter((x) => x.id !== pieceId);
  return retirerZonesVides(e);
}

/** Déplace un local vers une autre zone (la zone quittée disparaît si elle est vide). */
export function deplacerPiece(etat: ProjetCvc, pieceId: string, zoneId: string): ProjetCvc {
  const e = copie(etat);
  const p = e.pieces.find((x) => x.id === pieceId);
  if (!p || !e.zones.some((z) => z.id === zoneId)) return e;
  p.zone_id = zoneId;
  return retirerZonesVides(e);
}

export function ajouterVitrage(etat: ProjetCvc, pieceId: string): ProjetCvc {
  const e = copie(etat);
  const p = e.pieces.find((x) => x.id === pieceId);
  if (!p || (p.vitrages ?? []).length >= MAX.vitrages) return e;
  p.vitrages = [...(p.vitrages ?? []), { id: nouvelId('v'), nom: '', orientation: 'Sud', largeur_m: null, hauteur_m: null, nombre: 1, vitrage: p.vitrage_defaut, protection: 'aucune', irradiance_w_m2: null }];
  return e;
}

export function supprimerVitrage(etat: ProjetCvc, pieceId: string, vitrageId: string): ProjetCvc {
  const e = copie(etat);
  const p = e.pieces.find((x) => x.id === pieceId);
  if (p) p.vitrages = (p.vitrages ?? []).filter((v) => v.id !== vitrageId);
  return e;
}

export function ajouterGroupe(etat: ProjetCvc): ProjetCvc {
  const e = copie(etat);
  if (e.groupes.length >= MAX.groupes) return e;
  e.groupes.push({ id: nouvelId('g'), nom: 'Groupe extérieur ' + (e.groupes.length + 1), zones: [] });
  return e;
}

/** Rattache une zone à un groupe extérieur (une zone n'appartient qu'à un groupe) ; '' = aucun groupe. */
export function rattacherZone(etat: ProjetCvc, zoneId: string, groupeId: string): ProjetCvc {
  const e = copie(etat);
  e.groupes = e.groupes.map((g) => ({ ...g, zones: g.id === groupeId ? [...new Set([...g.zones, zoneId])] : g.zones.filter((z) => z !== zoneId) }));
  return e;
}

export function supprimerGroupe(etat: ProjetCvc, groupeId: string): ProjetCvc {
  const e = copie(etat);
  if (e.groupes.length <= 1) return e;
  e.groupes = e.groupes.filter((g) => g.id !== groupeId);
  return e;
}

/* ------------------------------------- calcul ------------------------------------- */

const alerte = (code: string, niveau: Alerte['niveau'], message: string, cible?: string | null): Alerte => ({ code, niveau, message, cible: cible || null });

export interface EmetteurLocal {
  id: string; nom: string; nominal_w: number; puissance_w: number | null; facteur: number | null; deperditions_w: number | null;
  couverture: number | null; refus: string | null;
}

export interface CalculCvc {
  etat: ProjetCvc;
  froids: (F.ResultatFroid | F.ResultatFroidIncomplet)[];
  chauds: C.ResultatChaud[];
  zones: ResultatZone[];
  groupes: ResultatGroupe[];
  emetteurs: EmetteurLocal[];
  resultats: ReturnType<typeof resumer>;
  intermediaires: Record<string, any>;
  alertes: Alerte[];
  bloquants: Alerte[];
  hypotheses: string[];
}

function resumer(etat: ProjetCvc, zones: ResultatZone[], groupes: ResultatGroupe[], froids: CalculCvc['froids'], base: C.TemperatureBase | null) {
  const tot = totaux(zones, groupes);
  const ok = base && !base.refus ? base : null;
  return {
    schema: SCHEMA,
    objectif: etat.objectif,
    pieces: froids.filter((f) => !f.manques || !f.manques.length).length,
    pieces_saisies: etat.pieces.length,
    surface_m2: tot.surface_m2 || null,
    sensible_w: zones.length ? arrondi(tot.sensible_w, 0) : null,
    latent_w: zones.length ? arrondi(tot.latent_w, 0) : null,
    total_froid_w: zones.length ? arrondi(tot.total_froid_w, 0) : null,
    shr: tot.shr == null ? null : arrondi(tot.shr, 3),
    dimensionnement_froid_terminaux_w: zones.length ? arrondi(tot.dimensionnement_froid_terminaux_w, 0) : null,
    dimensionnement_froid_groupes_w: zones.length ? arrondi(tot.dimensionnement_froid_groupes_w, 0) : null,
    phi_chaud_w: tot.phi_w == null ? null : arrondi(tot.phi_w, 0),
    dimensionnement_chaud_w: tot.dimensionnement_chaud_w == null ? null : arrondi(tot.dimensionnement_chaud_w, 0),
    air_neuf_cta_w: tot.air_neuf_cta_w ? arrondi(tot.air_neuf_cta_w, 0) : null,
    theta_e_c: ok ? ok.theta_e : null,
    zone_climatique: ok ? ok.zone : null,
    theta_e_origine: ok ? ok.origine : null,
    theta_e_source: ok ? ok.source : null,
    ratio_w_m2: tot.surface_m2 ? arrondi(tot.total_froid_w / tot.surface_m2, 0) : null,
    zones: zones.map((z) => ({
      id: z.id, nom: z.nom, surface_m2: arrondi(z.surface_m2, 2), pieces: z.pieces.length, incomplet: z.incomplet,
      sensible_w: arrondi(z.sensible_w, 0), latent_w: arrondi(z.latent_w, 0), total_w: arrondi(z.total_w, 0),
      creneau: z.creneau, creneau_nom: z.creneau_nom, shr: z.shr == null ? null : arrondi(z.shr, 3),
      dimensionnement_froid_w: arrondi(z.dimensionnement_froid_w, 0),
      phi_w: z.phi_w == null ? null : arrondi(z.phi_w, 0),
      dimensionnement_chaud_w: z.dimensionnement_chaud_w == null ? null : arrondi(z.dimensionnement_chaud_w, 0),
      air_neuf_cta_w: z.air_neuf_cta_w ? arrondi(z.air_neuf_cta_w, 0) : null,
      economie_non_simultaneite_w: arrondi(z.economie_non_simultaneite_w, 0),
    })),
    groupes: groupes.map((g) => ({
      id: g.id, nom: g.nom, zones: g.zones.length, simultaneite: g.simultaneite,
      dimensionnement_terminaux_w: arrondi(g.dimensionnement_terminaux_w, 0),
      dimensionnement_froid_w: arrondi(g.dimensionnement_froid_w, 0),
      dimensionnement_chaud_w: g.dimensionnement_chaud_w == null ? null : arrondi(g.dimensionnement_chaud_w, 0),
    })),
    materiel_selectionne: false,
    statut_selection: 'a_verifier' as const,
  };
}

export function calculer(brut: unknown): CalculCvc {
  const etat = normaliser(brut);
  const alertes: Alerte[] = [];
  const hypotheses: string[] = [];
  const objectif = OBJECTIFS.find((o) => o.id === etat.objectif) || OBJECTIFS[1];
  const avecChaud = objectif.chaud;

  /* --- coefficients de sécurité et simultanéité --- */
  if (etat.securite.froid_pct > 20) alertes.push(alerte('F12', 'attention', 'Coefficient de sécurité en froid de ' + nf(etat.securite.froid_pct) + ' % : surdimensionnement probable (cycles courts, déshumidification dégradée).', 'securite'));
  if (avecChaud && etat.securite.chaud_pct > 20) alertes.push(alerte('F12', 'attention', 'Coefficient de sécurité en chaud de ' + nf(etat.securite.chaud_pct) + ' % : surdimensionnement probable.', 'securite'));
  if (etat.simultaneite.s < 1 && !etat.simultaneite.justification.trim()) {
    alertes.push(alerte('F08', 'bloquant', 'Coefficient de simultanéité inférieur à 1 : justifiez-le (usage réel des locaux) ou remettez-le à 1.', 'simultaneite'));
  }
  if (etat.batiment.altitude_m != null && etat.batiment.altitude_m > 1500) {
    alertes.push(alerte('F07', 'attention', 'Altitude supérieure à 1 500 m : hors domaine usuel, faites valider la température extérieure de base.', 'batiment'));
  }

  /* --- froid, local par local --- */
  const reglagesFroid = { ...etat.reglages, securite_pct: etat.securite.froid_pct };
  const froids = etat.pieces.map((p) => {
    try {
      return F.calculerPiece(p, etat.conditions, reglagesFroid);
    } catch (err: any) {
      return { id: p.id, nom: p.nom, count: p.count || 1, manques: [], erreur: true, hypotheses: [], alertes: [alerte('CALCUL', 'bloquant', p.nom + ' : ' + err.message, p.id)] } as F.ResultatFroidIncomplet;
    }
  });
  for (const f of froids) {
    alertes.push(...(f.alertes || []));
    hypotheses.push(...(f.hypotheses || []));
    if (f.manques && f.manques.length) {
      const libelles = [
        f.manques.includes('surface') ? 'surface (ou longueur et largeur)' : '',
        f.manques.includes('hauteur') ? 'hauteur sous plafond' : '',
        f.manques.includes('soufflage') ? 'état de soufflage de l’air neuf prétraité (température et humidité)' : '',
      ].filter(Boolean);
      alertes.push(alerte('DONNEE_MANQUANTE', 'bloquant', f.nom + ' : ' + libelles.join(' et ') + ' à compléter.', f.id));
    }
  }
  // B2 : local d'ERP occupé sans débit d'air neuf saisi — la charge de l'air neuf n'est pas comptée.
  for (const p of etat.pieces) {
    const type = F.TYPES_PIECE.find((t) => t.id === p.type);
    if (type?.erp && (p.occupants ?? 0) > 0 && !(p.ventilation_m3h && p.ventilation_m3h > 0)) {
      alertes.push(alerte('V01', 'attention', p.nom + ' : ' + p.occupants + ' occupants sans débit d’air neuf saisi — la charge de l’air neuf n’est pas comptée. Saisissez le débit prévu (CCTP, fiche de la CTA) : l’outil ne le déduit pas de l’effectif.', p.id));
    }
  }

  /* --- chaud, local par local --- */
  let base: C.TemperatureBase | null = null;
  let chauds: C.ResultatChaud[] = [];
  if (avecChaud) {
    base = C.temperatureDeBase({
      departement: etat.batiment.departement,
      altitude_m: etat.batiment.altitude_m,
      theta_e_force: etat.conditions.theta_e_force,
      source: etat.conditions.theta_e_source,
    });
    if (base.refus) {
      alertes.push(alerte(base.code, 'bloquant', base.message, base.code === 'F06' ? 'theta_e' : 'batiment'));
    } else {
      hypotheses.push(base.source);
      const reglagesChaud = { ...etat.reglages, securite_pct: etat.securite.chaud_pct, pont_thermique_w_m2k: (C.PONTS_THERMIQUES.find((x) => x.id === etat.batiment.ponts) || C.PONTS_THERMIQUES[0]).valeur };
      const b = base;
      chauds = etat.pieces.map((p) => {
        try {
          return C.calculerPiece(p, b, reglagesChaud);
        } catch (err: any) {
          return { id: p.id, nom: p.nom, count: p.count || 1, manques: [], erreur: true, hypotheses: [], alertes: [alerte('CALCUL', 'bloquant', p.nom + ' : ' + err.message, p.id)] };
        }
      });
      for (const c of chauds) {
        alertes.push(...(c.alertes || []));
        hypotheses.push(...(c.hypotheses || []));
      }
    }
  }

  /* --- zones et groupes --- */
  const utilisables = froids.filter(F.estComplet);
  const zones = etat.zones.map((z) => {
    const ids = etat.pieces.filter((p) => p.zone_id === z.id).map((p) => p.id);
    return calculerZone(
      z,
      utilisables.filter((f) => ids.includes(f.id)),
      chauds.filter((c) => ids.includes(c.id) && !c.refus && !c.erreur && (!c.manques || !c.manques.length)),
      { securite_froid_pct: etat.securite.froid_pct, securite_chaud_pct: etat.securite.chaud_pct },
    );
  }).filter((z) => z.pieces.length);
  const groupes = etat.groupes.map((g) => calculerGroupe(g, zones, etat.simultaneite)).filter((g) => g.zones.length);
  const horsGroupe = zones.filter((z) => !etat.groupes.some((g) => g.zones.includes(z.id)));
  for (const z of horsGroupe) {
    alertes.push(alerte('G01', 'attention', 'Zone « ' + z.nom + ' » rattachée à aucun groupe extérieur : elle compte dans le besoin des terminaux, pas dans celui des groupes.', z.id));
  }
  for (const z of zones) {
    if (z.economie_non_simultaneite_w > 1) {
      hypotheses.push('Zone « ' + z.nom + ' » : non-simultanéité solaire calculée (créneau ' + z.creneau_nom.toLowerCase() + ') — ' + nf(z.economie_non_simultaneite_w, 0) + ' W de moins que la somme des pointes.');
    }
  }
  if (etat.simultaneite.s < 1 && etat.simultaneite.justification.trim()) {
    hypotheses.push('Coefficient de simultanéité déclaré ' + nf(etat.simultaneite.s, 2) + ' sur le besoin des groupes : ' + etat.simultaneite.justification.trim());
  }

  /* --- B3 : émetteurs à eau existants face aux déperditions --- */
  const emetteurs: EmetteurLocal[] = [];
  const avecEmetteurs = etat.pieces.filter((p) => (p.emetteurs_nominal_w ?? 0) > 0);
  if (avecEmetteurs.length) {
    if (etat.emetteurs.depart_c == null || etat.emetteurs.retour_c == null) {
      alertes.push(alerte('E00', 'attention', 'Émetteurs à eau saisis sans régime d’eau (départ et retour) : leur puissance réelle n’est pas calculée.', 'emetteurs'));
    }
    const vus = new Set<string>();
    for (const p of avecEmetteurs) {
      const c = chauds.find((x) => x.id === p.id);
      const ambiance = c?.theta_int_c ?? p.theta_int_c ?? null;
      const r = etat.emetteurs.depart_c == null || etat.emetteurs.retour_c == null ? null : C.puissanceEmetteur({
        nominale_w: p.emetteurs_nominal_w, depart_c: etat.emetteurs.depart_c, retour_c: etat.emetteurs.retour_c,
        ambiance_c: ambiance ?? 20, exposant: etat.emetteurs.exposant, moyenne: etat.emetteurs.moyenne,
        nominal: { depart_c: etat.emetteurs.nominal_depart_c, retour_c: etat.emetteurs.nominal_retour_c, ambiance_c: etat.emetteurs.nominal_ambiance_c },
      });
      const dep = c && c.phi_w != null && !c.refus ? c.phi_w : null;
      if (r && r.refus && !vus.has(r.code)) { vus.add(r.code); alertes.push(alerte(r.code, 'attention', r.message, 'emetteurs')); }
      const ok = r && !r.refus ? r : null;
      if (ok) for (const h of ok.hypotheses) hypotheses.push(h);
      if (ok && ambiance == null) hypotheses.push('Émetteurs de « ' + p.nom + ' » : ambiance prise à 20 °C (température intérieure non déterminée).');
      const couverture = ok && dep ? ok.puissance_w / dep : null;
      if (ok && dep != null && couverture != null && couverture < 1) {
        alertes.push(alerte('E02', 'attention', p.nom + ' : émetteurs de ' + nf(ok.puissance_w, 0) + ' W au régime ' + nf(etat.emetteurs.depart_c, 1) + '/' + nf(etat.emetteurs.retour_c, 1)
          + ' °C pour ' + nf(dep, 0) + ' W de déperditions (' + nf(couverture * 100, 0) + ' %) : insuffisants, à remplacer ou compléter, ou régime d’eau à relever.', p.id));
      }
      emetteurs.push({ id: p.id, nom: p.nom, nominal_w: p.emetteurs_nominal_w!, puissance_w: ok ? arrondi(ok.puissance_w, 0) : null,
        facteur: ok ? arrondi(ok.facteur, 3) : null, deperditions_w: dep == null ? null : arrondi(dep, 0),
        couverture: couverture == null ? null : arrondi(couverture, 3), refus: r && r.refus ? r.message : null });
    }
  }

  if (!etat.reglages.confirmees) alertes.push(alerte('REGLAGES_EXEMPLE', 'attention', 'Hypothèses de calcul encore sur les valeurs par défaut du logiciel : vérifiez-les, puis cochez la case des hypothèses vérifiées.', 'reglages'));
  alertes.push(alerte('MATERIEL_ABSENT', 'attention', 'Sélection du matériel non faite par l’outil : aucun catalogue constructeur vérifié n’est raccordé. La puissance calculée ne vaut pas choix de référence — validez les performances aux conditions du projet sur la notice.', 'materiel'));

  const resultats = resumer(etat, zones, groupes, froids, base);

  // Données intermédiaires : compactes et relisibles — termes par local, pas de tableau géant.
  const premier = froids.find(F.estComplet);
  const intermediaires = {
    schema: SCHEMA,
    conditions: premier ? { exterieur: premier.exterieur, interieur: premier.interieur, source: etat.conditions.source } : { source: etat.conditions.source },
    theta_e: base && !base.refus ? { valeur_c: base.theta_e, zone: base.zone, origine: base.origine, source: base.source } : null,
    pieces: froids.filter(F.estComplet).map((f) => ({
      id: f.id, nom: f.nom, count: f.count, surface_m2: arrondi(f.surface_m2, 2),
      froid: {
        rangs_sensibles: (f.rangs_sensibles || []).map((r) => [r[0], arrondi(r[1], 0)]),
        rangs_latents: (f.rangs_latents || []).map((r) => [r[0], arrondi(r[1], 0)]),
        sensible_w: arrondi(f.sensible_w, 0), latent_w: arrondi(f.latent_w, 0), total_w: arrondi(f.total_w, 0),
        solaire_creneaux_w: (f.solaire_creneaux_w || []).map((x) => arrondi(x, 0)), creneau: f.creneau,
        shr: f.shr == null ? null : arrondi(f.shr, 3), ratio_w_m2: arrondi(f.ratio_w_m2, 0),
        debit_air_m3h: arrondi(f.debit_air_m3h, 1), coefficients: f.coefficients,
        apports_occupant: f.apports_occupant,
        air_neuf_cta: f.air_neuf_cta ? { total_w: arrondi(f.air_neuf_cta.total_w, 0), sensible_w: arrondi(f.air_neuf_cta.sensible_w, 0), latent_w: arrondi(f.air_neuf_cta.latent_w, 0) } : null,
      },
      chaud: (() => {
        const c = chauds.find((x) => x.id === f.id);
        if (!c || c.refus || c.erreur || (c.manques && c.manques.length)) return null;
        return {
          theta_int_c: c.theta_int_c, delta_t_k: arrondi(c.delta_t_k, 1),
          h_t_w_k: arrondi(c.h_t_w_k, 2), h_t_parois_w_k: arrondi(c.h_t_parois_w_k, 2), h_t_ponts_w_k: arrondi(c.h_t_ponts_w_k, 2), h_v_w_k: arrondi(c.h_v_w_k, 2),
          parois: (c.parois ?? []).map((p) => ({ nom: p.nom, u: p.u, surface_m2: arrondi(p.surface_m2, 2), b: p.b, h_w_k: arrondi(p.h_w_k, 2) })),
          phi_w: arrondi(c.phi_w, 0), ratio_w_m2: arrondi(c.ratio_w_m2, 0),
        };
      })(),
    })),
    zones: zones.map((z) => ({
      id: z.id, nom: z.nom, pieces: z.pieces,
      solaire_creneaux_w: z.solaire_creneaux_w.map((x) => arrondi(x, 0)), creneau: z.creneau,
      total_pointes_additionnees_w: arrondi(z.total_pointes_additionnees_w, 0),
      economie_non_simultaneite_w: arrondi(z.economie_non_simultaneite_w, 0),
    })),
    groupes: groupes.map((g) => ({ id: g.id, nom: g.nom, zones: g.zones, simultaneite: g.simultaneite, dimensionnement_terminaux_w: arrondi(g.dimensionnement_terminaux_w, 0), dimensionnement_froid_w: arrondi(g.dimensionnement_froid_w, 0) })),
  };

  return {
    etat, froids, chauds, zones, groupes, emetteurs, resultats, intermediaires,
    alertes, bloquants: alertes.filter((a) => a.niveau === 'bloquant'), hypotheses: [...new Set(hypotheses)],
  };
}

/** Mentions portées par la note de calcul et l'écran (faits, pas d'argument commercial). */
export const MENTIONS = Object.freeze([
  'Pré-dimensionnement d’assistance, à vérifier par un professionnel qualifié : ce n’est ni un calcul réglementaire, ni une étude thermique, ni une simulation dynamique.',
  'Bilan stationnaire (pas d’inertie, pas de profil horaire) ; psychrométrie selon les corrélations ASHRAE Fundamentals 2017 réimplémentées.',
  'Température extérieure de base issue de la table publique du §18.1 de la méthode 3CL-DPE 2021 (arrêté du 31 mars 2021) ; les autres valeurs par défaut sont des hypothèses logiciel modifiables.',
  'Aucun matériel n’est sélectionné par l’outil : la puissance calculée ne vaut pas choix de référence. Les performances aux conditions réelles du projet se lisent sur la notice du constructeur.',
  'Au-delà de 4 m de hauteur, la stratification de l’air n’est pas calculée (alerte F04).',
]);
